"use client";

import React from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { SidebarFilter, FilterOption } from '@/components/molecules/Filter';
import { SearchCard } from '@/components/molecules/SearchCard';
import CardNewsList from '@/components/molecules/CardNewsList';
import PageSizeSelector from '@/components/atoms/PageSizeSelector';

import { Pagination } from "@/components/molecules/Pagination";
import { LoadingSpinner } from "@/components/atoms/LoadingSpinner";
import { ICMSNoticia, IPagination } from '@/services/types';


type NoticiaOrEvento = ICMSNoticia & { tipo: string };

interface NoticiasContentProps {
  items: NoticiaOrEvento[];
  pagination: IPagination;
  filters: FilterOption[]; // Now expects array of FilterOption from CMS
  itemsPerPage: number;
  loading?: boolean;
}

export default function NoticiasContent({
  items,
  pagination,
  filters,
  itemsPerPage,
  loading = false,
}: NoticiasContentProps) {
  const [isFilterVisible, setIsFilterVisible] = React.useState(true);
  const [showMobileFilters, setShowMobileFilters] = React.useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();


  const handleToggleFilter = (isVisible: boolean) => {
    setIsFilterVisible(isVisible);
  };
  // Todos os handlers e hooks de fetch/paginação/filtros foram movidos para o server/page. Aqui só apresentação.
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Desktop Sidebar Filter */}
          <div className={`hidden lg:block w-full transition-all pt-6 duration-300 ${isFilterVisible ? 'lg:w-1/4' : 'lg:w-auto'}`}>
            <SidebarFilter data={filters} onToggleDesktopFilter={handleToggleFilter} />
            {isFilterVisible && (
              <div>
                <button
                  onClick={() => { window.location.href = '/noticias-eventos'; }}
                  className="bg-[#C3C1C0] text-redief font-medium px-6 py-3 mt-8 rounded-full hover:bg-[#b3b1b0] transition-colors w-full lg:w-auto"
                >
                  Limpar filtros
                </button>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className={`w-full transition-all duration-300 ${isFilterVisible ? 'lg:w-3/4' : 'lg:w-full'}`}>
            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-6 lg:gap-12 px-2 sm:px-4 py-2 sm:py-6">
              <div className="flex-1 max-w-full sm:max-w-[500px] lg:max-w-[710px] lg:ml-auto">
                <SearchCard
                  onOpenMobileFilters={() => setShowMobileFilters(true)}
                />
              </div>
              <div className="flex-shrink-0 self-end sm:self-center">
                <PageSizeSelector
                  className="w-auto ml-auto"
                  value={itemsPerPage}
                  options={[9, 12, 15]}
                  onChange={(newPageSize: number) => {
                    const params = new URLSearchParams(searchParams?.toString() || '');
                    params.set('pageSize', String(newPageSize));
                    params.set('page', '1');
                    router.push(`${pathname}?${params.toString()}`);
                  }}
                />
              </div>
            </div>

            {/* Notícias e Eventos Grid */}
            <div className="px-2 sm:px-4">
              {loading ? (
                <LoadingSpinner message="Carregando notícias e eventos..." className="py-12" />
              ) : (items.length > 0) ? (
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 transition-all duration-300 ${isFilterVisible ? 'lg:grid-cols-2' : 'lg:grid-cols-3'
                  }`}>
                {items
                  .map((item, index) => (
                    <CardNewsList
                      key={`${item.tipo || 'item'}-${item.documentId || index}`}
                      noticia={{
                        titulo: item.title || '',
                        descricao: item.description || item.description || '',
                        documentId: item.documentId || '',
                        dataPublicacao: item.createdAt || item.publishedAt || '',
                        slug: item.slug || '',
                        imgUrl: (
                          item.tipo === 'noticia'
                            ? (
                                (item as any)?.image?.formats?.medium?.url ||
                                (item as any)?.image?.url ||
                                (item as any)?.image?.formats?.small?.url ||
                                (item as any)?.image?.formats?.thumbnail?.url ||
                                '/banner.png'
                              )
                            : (
                                item.image?.url || '/banner.png'
                              )
                        ),
                        categoria: (item as any).categoria ||/*
                          (typeof item.cms_tema_noticia === 'string'
                            ? item.cms_tema_noticia
                            : item.cms_tema_noticia?.label) ||*/ '',
                        tipo: item.tipo, // 'noticia' ou 'evento'
                        // Campos específicos de eventos
                        tipo_evento: (item as any).tipo_evento,
                        data_evento: (item as any).data_evento,
                        local_evento: (item as any).local_evento,
                        cms_tema_noticia: item.tipo,
                        cms_estado_evento: (item as any).cms_estado_evento,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-[#C3C1C0] text-lg">Nenhuma notícia ou evento encontrado com os filtros selecionados.</p>
                  <button
                    onClick={() => { window.location.href = '/noticias-eventos'; }}
                    className="mt-4 px-6 py-2 bg-[#F9F7F7] text-white rounded-md hover:bg-[#F9F7F7] transition-colors"
                    aria-label="Limpar todos os filtros"
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>

            {/* Paginação Fixa */}
            {!loading && pagination.pageCount > 1 && (
              <div className="mt-8 sm:mt-12 flex justify-center z-10">
                <div className="rounded-full px-4 py-2">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.pageCount}
                    onPageChange={(page) => {
                      if (page >= 1 && page <= pagination.pageCount && page !== pagination.page) {
                        const params = new URLSearchParams(searchParams?.toString() || '');
                        params.set('page', String(page));
                        router.push(`${pathname}?${params.toString()}`);
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Mobile Filter Modal */}
        {showMobileFilters && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center lg:hidden">
            <div className="bg-white w-[90%] max-w-md h-[90%] max-h-[600px] rounded-lg overflow-hidden">
              <SidebarFilter
                data={filters}
                isMobile={true}
                onCloseMobile={() => setShowMobileFilters(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
